import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import Hero from "@/components/sections/Hero";
import Stats from "@/components/sections/Stats";
import Benefits from "@/components/sections/Benefits";
import Partners from "@/components/sections/Partners";
import Pricing from "@/components/sections/Pricing";
import Process from "@/components/sections/Process";
import Countries from "@/components/sections/Countries";
import Vacancies from "@/components/sections/Vacancies";
import Team from "@/components/sections/Team";
import Reviews from "@/components/sections/Reviews";
import Contacts from "@/components/sections/Contacts";
import { useContent } from "@/lib/content";

const SECTIONS = {
  stats: Stats,
  benefits: Benefits,
  partners: Partners,
  pricing: Pricing,
  process: Process,
  countries: Countries,
  vacancies: Vacancies,
  team: Team,
  reviews: Reviews,
  contacts: Contacts,
};

const DEFAULT_ORDER = ["stats", "benefits", "partners", "pricing", "process", "countries", "vacancies", "team", "reviews", "contacts"];

export default function HomePage() {
  const { content, loading } = useContent();
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth" }), 200);
    }
  }, [location]);

  if (loading) {
    return (
      <div className="App">
        <Header />
        <main>
          {/* Skeleton hero so the page never looks broken/empty during initial load */}
          <section className="pt-28 pb-16 sm:pt-32 sm:pb-20 lg:pt-44 lg:pb-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
              <div className="space-y-5">
                <div className="h-7 w-56 rounded-full bg-muted animate-pulse" />
                <div className="h-12 sm:h-16 w-full rounded-xl bg-muted animate-pulse" />
                <div className="h-12 sm:h-16 w-4/5 rounded-xl bg-muted animate-pulse" />
                <div className="h-6 w-3/4 rounded-lg bg-muted animate-pulse" />
                <div className="flex gap-3 pt-2">
                  <div className="h-12 w-44 rounded-full bg-muted animate-pulse" />
                  <div className="h-12 w-40 rounded-full bg-muted animate-pulse" />
                </div>
              </div>
              <div className="hidden lg:flex items-center justify-center">
                <div className="size-80 rounded-full bg-muted animate-pulse" />
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  // Defensive: only filter if visibility data has the expected shape.
  // If it's malformed (e.g. accidentally overwritten by another block), show everything.
  const rawVisibility = content?.visibility;
  const isValidVisibility = rawVisibility && typeof rawVisibility === "object"
    && DEFAULT_ORDER.some((k) => k in rawVisibility);
  const visibility = isValidVisibility ? rawVisibility : {};

  return (
    <div className="App">
      <Header />
      <main>
        <Hero />
        {DEFAULT_ORDER.map((key) => {
          if (visibility[key] === false) return null;
          const Section = SECTIONS[key];
          return Section ? <Section key={key} /> : null;
        })}
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}
